import dayjs from 'dayjs';
import { CalendarOptions } from './constants/constants';

export interface CalendarEvent {
  details?: string;
  duration?: string;
  endDate?: Date | string;
  location?: string;
  startDate?: Date | string;
  subject?: string;
  timezone?: string;
}

export const formatCalendarDate = (date?: string | Date) =>
  dayjs(date).format('YYYYMMDD');

export const formatStartTimeZoneIcs = (
  startDate: CalendarEvent['startDate'],
  timezone?: string
) =>
  timezone ? `DTSTART;TZID=${timezone}:${startDate}` : `DTSTART:${startDate}`;

export const formatEndTimeZoneIcs = (
  endDate: CalendarEvent['endDate'],
  timezone?: string
) => (timezone ? `DTEND;TZID=${timezone}:${endDate}` : `DTEND:${endDate}`);

export const generateICS = ({
  startDate,
  endDate,
  subject,
  details,
  timezone,
  location,
}: CalendarEvent) => {
  const content = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    `URL:${document.URL}`,
    'METHOD:PUBLISH',
    `LOCATION: ${location}`,
    formatStartTimeZoneIcs(startDate, timezone),
    endDate ? formatEndTimeZoneIcs(endDate, timezone) : '',
    `SUMMARY:${subject}`,
    `X-ALT-DESC;FMTTYPE=text/html: ${details}`,
    `DESCRIPTION:${details}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\n');

  return content;
};

export const generateGoogleCalendarEvent = ({
  details,
  subject,
  location,
  startDate,
  endDate,
}: CalendarEvent) => {
  const BASE_URL = 'https://calendar.google.com/calendar/render';

  return `${BASE_URL}?action=TEMPLATE&text=${subject}&dates=${startDate}/${endDate}&details=${details}&location=${location}`;
};

export const buildCalendarUrl = ({
  event,
  type,
}: {
  event: CalendarEvent;
  type: string;
}) => {
  const formatStartDate = formatCalendarDate(event.startDate);
  const formatEndDate = event.endDate
    ? formatCalendarDate(event.endDate)
    : dayjs(event.startDate).add(1, 'day').format('YYYYMMDD');

  const data = {
    startDate: formatStartDate,
    endDate: formatEndDate,
    location: event.location || '',
    subject: event.subject,
    details: event.details || '',
  };

  if (type === CalendarOptions.google) {
    return generateGoogleCalendarEvent(data);
  }
  return generateICS(data);
};
